import { Renderer } from '../../Renderer';
import { Camera } from '../../core/Camera';
import {
  Box3,
  Dispatcher,
  Frustum,
  Matrix4,
  Vector2,
  Vector3,
  WebGPUCoordinateSystem,
} from 'rewild-common';
import { TerrainChunk, TerrainChunkEvent } from './TerrainChunk';
import { LODMesh } from './LODMesh';
import { TerrainWorkerPool } from './TerrainWorkerPool';
import {
  ClimateConfig,
  DEFAULT_CLIMATE_PRESET,
  MAX_SPLAT_LAYERS,
  getClimatePalette,
  resolveClimatePreset,
} from './Biomes';
import { ChunkSnapshotProvider } from './ChunkSnapshot';
import { PaintMaskProvider } from './PaintMask';
import { WaterEditProvider } from './WaterEdit';
import { WaterBodyProvider } from './WaterBodies';
import { WaterBodyRules } from './WaterBodyRules';
import { ScatterKillSet, ScatterKillSetProvider } from './ScatterKillSet';
import { ScatterInstances, ScatterPick, pickScatterInstance } from './Scatter';
import { generateSplatMap, sampleSplatMap } from './Splat';
import { TERRAIN_MATERIALS } from './TerrainMaterials';
import { TERRAIN_METERS_PER_SAMPLE } from './MeshGenerator';
import { GroundSampler, createGroundSampler, sampleGround } from './Noise';
import { ScatterModels } from './ScatterModels';
import {
  CHUNK_MASK_SIZE,
  HorizonOcean,
  chunkMaskIndex,
} from '../water/HorizonOcean';
import { WaterWaveBuffer } from '../water/WaterWaves';
import { underWaterQuality, waterDetailBias } from '../water/WaterQuality';
import { OceanFFT } from '../water/OceanFFT';
import { SeaSpray } from '../water/SeaSpray';
import { WaterProbe } from '../water/WaterProbe';
import { UnderWater, clipNear } from '../water/UnderWater';
import { UnderWaterFog } from '../water/UnderWaterFog';
import { WaterLens, lensRain } from '../water/WaterLens';
import { rainBelowDeck } from '../sky/CloudDeck';
import { CAUSTIC_CASCADE, Caustics } from '../water/Caustics';
import { LightShafts } from '../water/LightShafts';
import { MarineSnow } from '../water/MarineSnow';
import { GpuPassTimer } from '../../metrics/GpuPassTimer';
import { WaterQuery, WaterQuerySource } from '../water/WaterQuery';
import { WATER_MAP_STEP, WaterMap } from './WaterMap';
import { shoreWaveHeight } from '../water/ShoreWaves';
import { SHORE_FIELD_SPAN, ShoreField } from '../water/ShoreField';
import { WaterLevelField } from '../water/WaterLevelField';
import { MAX_WATER_TYPES, OCEAN_WATER } from './Water';
import { MAX_WATER_GRID_BANDS, waterGridBands } from '../water/WaterGrid';

export class LODInfo {
  lod: i32;
  visibleDstThreshold: f32;
}

export type TerrainEvent =
  | { type: 'chunk-loaded'; chunk: TerrainChunk; lod: LODMesh }
  | {
      type: 'scatter-loaded';
      chunk: TerrainChunk;
      instances: ScatterInstances[];
    }
  | { type: 'chunk-unloaded'; chunk: TerrainChunk }
  | { type: 'chunk-disposed'; chunk: TerrainChunk }
  /** The camera is high enough that the horizon ring draws all the ground and
   *  no chunk will load. Raised once per chunk set while it holds. */
  | { type: 'far-only' };

const viewerMoveThresholdForChunkUpdate = 25;
const sqrViewerMoveThresholdForChunkUpdate =
  viewerMoveThresholdForChunkUpdate * viewerMoveThresholdForChunkUpdate;

// Re-run chunk selection when the camera turns enough that frustum membership
// could change, even if the player hasn't moved. Facing is compared by dot
// product of unit view directions, so this is cos(~3°).
const viewDirDotThresholdForChunkUpdate = Math.cos(0.05);

// Vertical half-height of the box a chunk is frustum-tested with. A chunk's
// `bounds` are deliberately flat (y=0) for the horizontal nearest-edge metric,
// but terrain has real relief and the camera tilts, so pad generously in y to
// avoid culling a chunk whose surface is actually in view.
const CULL_BOX_HALF_HEIGHT: f32 = 1000;

// Radians the view chunks are generated for reaches past the camera's on each
// side, so the chunks a turn brings in are usually meshed before they reach
// the screen rather than showing sky while they stream in.
const GENERATION_VIEW_MARGIN = (25 * Math.PI) / 180;

// Metres above farOnlyAbove the camera climbs before the chunks are dropped.
const FAR_ONLY_MARGIN = 100;

/** Widens a perspective projection by `margin` radians each side, in place.
 *  An orthographic one is left as it is. */
export function widenProjection(projection: Matrix4, margin: number): void {
  const e = projection.elements;
  if (e[11] === 0) return;
  e[0] = widenScale(e[0], margin);
  e[5] = widenScale(e[5], margin);
}

// A projection's scale on one axis is 1 / tan(half the view angle).
function widenScale(scale: number, margin: number): number {
  const half = Math.atan(1 / Math.abs(scale));
  const widened = Math.min(half + margin, Math.PI / 2 - 0.01);
  return Math.sign(scale) / Math.tan(widened);
}

// Reusable scratch — chunk selection runs every frame the view changes, so it
// must not allocate (see project note on per-frame allocation).
const _projScreenMatrix = new Matrix4();
const _generationProj = new Matrix4();
const _frustum = new Frustum();
const _cullBox = new Box3();
const _viewDir = new Vector3();

export class TerrainRenderer implements WaterQuerySource {
  viewPosOld: Vector3;
  // Camera facing at the last chunk-selection pass — a turn past
  // viewDirDotThresholdForChunkUpdate re-runs selection even without movement,
  // since which chunks are in the frustum depends on facing.
  viewDirOld: Vector3;
  // The camera on the ground plane. Chunk streaming, LOD and eviction measure
  // against flat chunk footprints, so the camera's height must not count.
  viewerPosition: Vector3;
  // The camera itself, for what has real 3D bounds: the scatter tier cull,
  // which would otherwise read every layer on a mountain as a hundred metres
  // off and never reach its near tiers.
  viewerEye: Vector3;
  chunkSize: i32;
  chunksVisibleInViewDst: i32;
  terrainChunks: Map<string, TerrainChunk>;
  terrainChunksVisibleLastUpdate: TerrainChunk[];
  private readonly previouslyVisibleChunks: TerrainChunk[] = [];
  private visibilityPass = 0;
  // Distance-banded LODs, nearest to farthest. `visibleDstThreshold` is the
  // nearest-edge WORLD distance past which the *next* (coarser) LOD takes over;
  // the last entry's threshold is maxViewDst, the terrain's visible radius. This
  // — not the camera far plane and not metersPerSample — is what sets how far
  // the horizon reaches: a chunk past maxViewDst is never generated.
  //
  // lod maps to mesh resolution as inc = lod*2 (MeshGenerator), so inc must
  // divide the sample span chunkSize-1 (240): valid inc are 1,2,4,6,8,10,12,16,
  // 20,24 (lod = inc/2; e.g. lod 7 / inc 14 is invalid — 240/14 isn't integer).
  //
  // maxViewDst is 2800. With chunks (240 * metersPerSample = 480) wide, the
  // worst-case far chunk corner sits ~3480 out, inside the 4000 camera far
  // plane with margin (raising the far plane does NOT affect shadows — they are
  // capped at SHADOW_CASCADE_FAR). Ceiling is ~3200 before corners risk the far
  // plane; beyond that, raise the far plane first. lod 8/10 (inc 16/20) are the
  // cheap far rings that carry the extra distance without many verts.
  detailLevels: LODInfo[] = [
    { lod: 0, visibleDstThreshold: 200 },
    { lod: 1, visibleDstThreshold: 400 },
    { lod: 2, visibleDstThreshold: 600 },
    { lod: 3, visibleDstThreshold: 800 },
    { lod: 4, visibleDstThreshold: 1000 },
    { lod: 5, visibleDstThreshold: 1300 },
    { lod: 6, visibleDstThreshold: 1700 },
    { lod: 8, visibleDstThreshold: 2200 },
    { lod: 10, visibleDstThreshold: 2800 },
  ];
  dispatcher: Dispatcher<TerrainEvent>;
  workerPool: TerrainWorkerPool;
  // Geometry, passes and node transforms per scatter layer, shared by every
  // chunk that grows one.
  scatterModels: ScatterModels;
  // Whole tiers every scatter instance is pushed along its layer's LOD chain:
  // positive coarser, negative finer. Debug lever; 0 draws as authored.
  scatterLodBias = 0;
  // Tint scatter by LOD tier so handover distances can be seen rather than
  // argued about.
  scatterLodTint = false;
  private onChunkLoadedDelegate: (event: TerrainChunkEvent) => void;
  private _needsVisibilityUpdate: boolean = false;
  // Captured in init(); background mesh refreshes (edits) need it outside the
  // update() call path.
  private renderer: Renderer | null = null;

  _hasInitiallyUpdatedTerrain: boolean = false;
  readonly mapChunkSizeLod = 241;
  private _levelOfDetail: number = 0; // Must be any int from 0 to 6

  private _seed: number = 100;
  private _climatePreset: string = DEFAULT_CLIMATE_PRESET;
  private _splatPalette: readonly string[] = [];
  private _splatPalettePreset: string | null = null;
  // `slip` of each splat channel's material, and the weights sampleSlip reads.
  private readonly _splatSlips = new Float32Array(MAX_SPLAT_LAYERS);
  private readonly _slipWeights = new Float32Array(MAX_SPLAT_LAYERS);
  private _seaLevel: number = 0;
  // Injected by the host app (game/editor); looks up a chunk's saved snapshot
  // heights on the asset path. Saved ⇒ meshed from storage, absent ⇒ generated.
  snapshotProvider: ChunkSnapshotProvider | null = null;
  // Same, for a chunk's saved biome paint mask. Absent ⇒ the chunk surfaces
  // from pure climate.
  biomeMaskProvider: PaintMaskProvider | null = null;
  // Saved scatter density masks, looked up per chunk on first placement.
  scatterMaskProvider: PaintMaskProvider | null = null;
  // Saved water edits, looked up per chunk on its first build.
  waterEditProvider: WaterEditProvider | null = null;
  // Saved kill sets, looked up alongside the density mask.
  scatterKillProvider: ScatterKillSetProvider | null = null;
  /** The lake edit rules and the level's water body records. */
  readonly waterRules = new WaterBodyRules(this);
  private _enabled: boolean = true;
  /** Camera metres above the ground at which chunks have faded out to the
   *  horizon ring. A little past it the terrain goes far-only: no chunks
   *  stream or draw, and the ring draws all the ground. */
  farOnlyAbove = 1500;
  /** Camera metres above the ground under which chunks are fully faded in. */
  farOnlyBelow = 1200;
  private _farOnly = false;
  private _farOnlyReported = false;
  // How far chunks have faded in by the camera's height, 0 to 1.
  private heightFade = 1;
  private fadeFrame = 0;
  // The generator's ground, for the camera's height above it without chunks.
  private groundSampler: GroundSampler | null = null;
  // The ocean past the last chunk. Created with the renderer.
  private horizonOcean: HorizonOcean | null = null;
  /** What every chunk's water shares besides the ocean, once a frame. */
  readonly waterWaves = new WaterWaveBuffer();
  /** The FFT ocean every chunk's water samples; made on the first update. */
  ocean: OceanFFT | null = null;
  seaSpray: SeaSpray | null = null;
  /** The water at a point for gameplay, with the drawn waves' height. */
  readonly waterQuery = new WaterQuery(this);
  private waterProbe: WaterProbe | null = null;
  /** The water around the camera. Its uniform outlives the chunks, so the
   *  passes that bind it are not rebuilt when the terrain resets. */
  readonly underWater = new UnderWater();
  private underWaterFog = new UnderWaterFog();
  /** Water on the camera's lens: blur, the waterline and drops. */
  readonly waterLens = new WaterLens();
  /** The sun focused by the waves under water. Its texture and params
   *  outlive the chunks, as the material passes bind them once. */
  readonly caustics = new Caustics();
  /** The water over the ground around the camera, for materials under it.
   *  Its textures outlive the chunks, as the material passes bind them once. */
  readonly waterLevels = new WaterLevelField();
  readonly lightShafts = new LightShafts();
  readonly marineSnow = new MarineSnow();
  // GPU time of the ocean's transforms, the caustics, the light shafts and
  // the lens (gpu/water). A timer per command buffer they run in.
  private oceanTimer: GpuPassTimer | null = null;
  private causticsTimer: GpuPassTimer | null = null;
  private shaftsTimer: GpuPassTimer | null = null;
  private lensTimer: GpuPassTimer | null = null;
  private toSun = new Float64Array(3);
  private sunRadiance = new Float64Array(3);
  /** Whether the waves displace the water grid. Off leaves them shading only,
   *  to tell a geometry artefact from a shading one. */
  waterWaveGeometry = true;
  /** Whether the waves shade the water's normal. Off leaves it flat. */
  waterWaveNormals = true;
  /** Paints the water's raw foam coverage in grey, to tell whether the ocean
   *  makes foam from whether the foam texture hides it. */
  waterFoamDebug = false;
  /** Strength of the sunlight through the wave crests; 1 is the default. */
  waterCrestGlow = 1;
  /** Scale on the far sea's roughness past the chunks; 1 matches the chunk
   *  water at range. */
  waterHorizonSlope = 1;
  private readonly horizonChunkMask = new Uint8Array(
    CHUNK_MASK_SIZE * CHUNK_MASK_SIZE
  );
  /** Strength of the trough darkening; 1 is the default. */
  waterTroughDarkening = 1;
  /** Scale on the shore waves' height; 1 is the default. */
  waterShoreWaves = 1;
  /** Paints the shore field on the water instead of shading it. */
  waterShoreDebug = false;
  /** A debug view of what the water's absorb draw reads, in place of the
   *  water: 0 off, 1 the depth facts, 2 the type weights, 3 the light that
   *  passes through (water.wgsl refractionDebug). */
  waterRefractionDebug = 0;
  /** Scale on the swash's runup; 1 is the default. */
  waterSwash = 1;
  /** Strength of the terrain's wet band; 1 is the default. */
  waterWetBand = 1;
  /** Where the ocean's shore waves run, around the camera. Made in init. */
  shoreField!: ShoreField;
  private sampleOceanDepthDelegate = this.sampleOceanDepth.bind(this);
  private waterMapAtDelegate = this.waterMapAt.bind(this);
  private oceanType = -1;
  private oceanChunk: TerrainChunk | null = null;
  private oceanChunkX = NaN;
  private oceanChunkY = NaN;
  // The heights and water each chunk last fed the water fields, so a LOD swap,
  // which changes neither, does not rebuild them.
  private waterFieldInputs = new WeakMap<
    TerrainChunk,
    { heights: Float32Array | null; version: number; water: WaterMap | null }
  >();
  private waterLodDistances = new Float32Array(MAX_WATER_GRID_BANDS);
  private waterLodSpacings = new Float32Array(MAX_WATER_GRID_BANDS);
  // Resolved once per preset rather than every frame: resolving an unknown id
  // warns.
  private horizonClimate: { preset: string; climate: ClimateConfig } | null =
    null;

  constructor() {
    this.terrainChunks = new Map();
    this.viewerPosition = new Vector3();
    this.viewerEye = new Vector3();
    this.terrainChunksVisibleLastUpdate = [];
    this.dispatcher = new Dispatcher<TerrainEvent>();
    this.onChunkLoadedDelegate = this.onChunkLoaded.bind(this);
  }

  get seed() {
    return this._seed;
  }

  // Chunks capture the seed when they are created, so changing it must throw
  // away every existing chunk — otherwise chunks generated before a world's
  // seed is applied (e.g. during project load) keep default-seed terrain and
  // render disconnected from their later-generated neighbours.
  set seed(value: number) {
    if (this._seed === value) return;
    this._seed = value;
    this.clearChunks();
  }

  get climatePreset() {
    return this._climatePreset;
  }

  // Same capture semantics as `seed` — a preset change invalidates all chunks.
  set climatePreset(value: string) {
    if (this._climatePreset === value) return;
    this._climatePreset = value;
    this.clearChunks();
  }

  // Saved water body records for the level, read on first use.
  set waterBodyProvider(provider: WaterBodyProvider | null) {
    this.waterRules.setProvider(provider);
  }

  get seaLevel() {
    return this._seaLevel;
  }

  // Same capture semantics as `seed` — the sea bed is generated below it.
  set seaLevel(value: number) {
    if (this._seaLevel === value) return;
    this._seaLevel = value;
    this.clearChunks();
  }

  get enabled() {
    return this._enabled;
  }

  // Runtime terrain gate (driven by ILevel.hasTerrain in the game; the editor
  // leaves it on). Disabling tears down any chunks already generated.
  set enabled(value: boolean) {
    if (this._enabled === value) return;
    this._enabled = value;
    if (!value) {
      this.clearChunks();
      this.horizonOcean?.hide();
    }
  }

  /** Whether the camera is high enough that only the horizon ring draws. */
  get farOnly() {
    return this._farOnly;
  }

  get maxViewDst() {
    return this.detailLevels.at(-1)!.visibleDstThreshold;
  }

  // Chunks are fully disposed once their nearest edge passes this distance.
  // The gap above maxViewDst is deliberate hysteresis: chunks in the band
  // (maxViewDst … evictDistance) keep their CPU heightfield/geometry while the
  // GPU buffers are freed, so backtracking a short way re-shows them without a
  // worker rebuild, and a player jittering across the view boundary doesn't
  // thrash create/dispose. 1.3× leaves ~2 chunks of slack (well above the
  // 25-unit move threshold) while holding far fewer trailing chunks resident
  // than the old 2× — the full-res heightfield + splat each retained chunk
  // carries (~0.45 MB CPU, independent of its LOD) is the terrain's dominant
  // memory cost, so the trailing band is where the savings are.
  get evictDistance() {
    return this.maxViewDst * 1.3;
  }

  // Chunks within this nearest-edge distance always load, ignoring the frustum,
  // so the ground under and around the player exists no matter which way the
  // camera faces. It matches the LOD-0 band exactly — LOD 0 is the only detail
  // level that produces a physics collider (see GameManager's chunk-loaded
  // handler), so every chunk that could carry the player must be in this set.
  get nearForceLoadDistance() {
    return this.detailLevels[0].visibleDstThreshold;
  }

  // World units per heightfield sample step. The heightfield is generated at
  // one sample per unit; this stretches the mesh/placement so a chunk spans
  // (samples-1) * metersPerSample world units with the same poly count. All of
  // this class's world math derives from `chunkSize`, so it flows through for
  // free; the value is surfaced here for the sculpt/editor coordinate mapping.
  get metersPerSample() {
    return TERRAIN_METERS_PER_SAMPLE;
  }

  // Assembles the (mapChunkSizeLod + 2)² apron an edited chunk's mesh is built
  // from: `inner` (the chunk's own heights) in the centre, plus a one-sample
  // ring of the four edge neighbours' current heights so edge-vertex normals are
  // two-sided and match the neighbour — otherwise sculpted borders show a dark
  // normal seam. A neighbour that isn't loaded (no heights) falls back to
  // duplicating this chunk's edge sample, i.e. a one-sided edge there. Corners
  // are left unset: the gradient only reads the 4-neighbourhood, never diagonals.
  //
  // Reads neighbours' live heights, so when a sculpt writes a shared edge both
  // chunks are re-meshed (the stamp's touched set) and each reads the other's
  // edit — seamless. Generated/unedited chunks don't use this; their worker-side
  // noise apron is already correct and needs no loaded neighbours.
  buildApron(inner: Float32Array, coord: Vector2): Float32Array {
    const size = this.mapChunkSizeLod; // samples per side (e.g. 241)
    const a = size + 2; // aproned side
    const apron = new Float32Array(a * a);
    const chunks = this.terrainChunks;
    const cx = coord.x;
    const cy = coord.y;
    const heightsAt = (x: number, y: number): Float32Array | null =>
      chunks.get(`${x},${y}`)?.heights ?? null;

    // Inner region → apron centre.
    for (let sy = 0; sy < size; sy++) {
      apron.set(inner.subarray(sy * size, sy * size + size), (sy + 1) * a + 1);
    }

    // Edge rings. +sx is +X, +sy is −Z, so the neighbour one step past +sy (the
    // top ring, sy = −1) is the chunk at cy + 1; see the mesh vertex placement.
    const left = heightsAt(cx - 1, cy);
    const right = heightsAt(cx + 1, cy);
    const top = heightsAt(cx, cy + 1);
    const bottom = heightsAt(cx, cy - 1);
    for (let sy = 0; sy < size; sy++) {
      const row = (sy + 1) * a;
      // sx = −1 is the left neighbour's second-to-last column (its last column
      // is our first); sx = size is the right neighbour's second column.
      apron[row] = left ? left[sy * size + (size - 2)] : inner[sy * size];
      apron[row + (a - 1)] = right
        ? right[sy * size + 1]
        : inner[sy * size + (size - 1)];
    }
    for (let sx = 0; sx < size; sx++) {
      apron[sx + 1] = top ? top[(size - 2) * size + sx] : inner[sx];
      apron[(a - 1) * a + (sx + 1)] = bottom
        ? bottom[size + sx]
        : inner[(size - 1) * size + sx];
    }

    return apron;
  }

  init(renderer: Renderer) {
    this.renderer = renderer;
    const mapChunkSize = this.mapChunkSizeLod;
    // World span of a chunk: (samples - 1) sample steps, each metersPerSample
    // world units wide. Every world-space calc below (chunk coords, cull box,
    // distances, sampleHeight) keys off this, so scaling is centralised here.
    this.chunkSize = (mapChunkSize - 1) * this.metersPerSample;
    // Chunks are culled by nearest-edge distance, so the creation square must
    // cover every chunk whose edge can fall within maxViewDst: a chunk at grid
    // offset k is at least (k-1)*chunkSize away from any viewer position
    // inside the current chunk.
    this.chunksVisibleInViewDst =
      Math.floor(this.maxViewDst / this.chunkSize) + 1;
    this.workerPool = new TerrainWorkerPool();
    this.scatterModels = new ScatterModels();
    this.horizonOcean = new HorizonOcean(renderer);
    this.shoreField = new ShoreField(renderer.device);
    this.oceanTimer = new GpuPassTimer(renderer.metrics, 'gpu/water');
    this.oceanTimer.init(renderer.device, ['ocean']);
    this.causticsTimer = new GpuPassTimer(renderer.metrics, 'gpu/water');
    this.causticsTimer.init(renderer.device, ['caustics']);
    this.shaftsTimer = new GpuPassTimer(renderer.metrics, 'gpu/water');
    this.shaftsTimer.init(renderer.device, ['shafts']);
    this.lensTimer = new GpuPassTimer(renderer.metrics, 'gpu/water');
    this.lensTimer.init(renderer.device, ['lens-frame', 'lens']);
  }

  /** Re-runs chunk and scatter visibility on the next update, without waiting
   *  for the camera to move. */
  requestVisibilityUpdate(): void {
    this._needsVisibilityUpdate = true;
  }

  private onChunkLoaded(event: TerrainChunkEvent) {
    this._needsVisibilityUpdate = true;
    this.invalidateWaterFields(event.chunk);
    // Note: a re-mesh (edit) raises chunk-loaded again for that LOD without a
    // preceding chunk-unloaded — the chunk never went away. Listeners holding
    // per-chunk resources built from a mesh must release the old one when they
    // (re)build on chunk-loaded, keyed by chunk id. Dispatching chunk-unloaded
    // here instead would be wrong and was actively harmful: a replaced LOD-3
    // mesh would tear down the LOD-0 physics collider, and the chunk-loaded
    // that followed carried lod 3, so the collider was never rebuilt — solid
    // looking terrain the player fell straight through.
    this.dispatcher.dispatch({
      type: 'chunk-loaded',
      chunk: event.chunk,
      lod: event.mesh,
    });
  }

  // Rebuilds the shore and water level fields over a chunk whose heights or
  // water changed since it last loaded.
  private invalidateWaterFields(chunk: TerrainChunk) {
    const seen = this.waterFieldInputs.get(chunk);
    if (
      seen &&
      seen.heights === chunk.heights &&
      seen.version === chunk.heightsVersion &&
      seen.water === chunk.water
    )
      return;
    if (seen) {
      seen.heights = chunk.heights;
      seen.version = chunk.heightsVersion;
      seen.water = chunk.water;
    } else
      this.waterFieldInputs.set(chunk, {
        heights: chunk.heights,
        version: chunk.heightsVersion,
        water: chunk.water,
      });

    const { min, max } = chunk.bounds;
    this.shoreField?.invalidate(min.x, min.z, max.x, max.z);
    this.waterLevels.invalidate(min.x, min.z, max.x, max.z);
  }

  get levelOfDetail() {
    return this._levelOfDetail;
  }

  set levelOfDetail(value: number) {
    this._levelOfDetail = value;

    if (this._levelOfDetail > 6) {
      this._levelOfDetail = 6;
    } else if (this._levelOfDetail < 0) {
      this._levelOfDetail = 0;
    }

    // Ensure its an integer
    this._levelOfDetail = Math.floor(this._levelOfDetail);
  }

  private updateVisibleChunks(renderer: Renderer) {
    // Chunks still in range stay visible throughout, and only those that left
    // it are hidden after: toggling a transform's visibility rebuilds the
    // scene BVH, so hiding and re-showing every chunk would rebuild it on
    // every update.
    const previous = this.previouslyVisibleChunks;
    previous.length = 0;
    for (const chunk of this.terrainChunksVisibleLastUpdate) previous.push(chunk);
    this.terrainChunksVisibleLastUpdate.length = 0;
    const pass = ++this.visibilityPass;

    const chunkSize = this.chunkSize;
    const half = chunkSize / 2;
    const maxViewDst = this.maxViewDst;
    const nearForceLoadDst = this.nearForceLoadDistance;
    const viewerX = this.viewerPosition.x;
    const viewerZ = this.viewerPosition.z;

    const currentChunkCoordX = Math.round(viewerX / chunkSize);
    const currentChunkCoordY = Math.round(viewerZ / chunkSize);

    const chunksVisibleInViewDst = this.chunksVisibleInViewDst;

    for (
      let yOffset = -chunksVisibleInViewDst;
      yOffset <= chunksVisibleInViewDst;
      yOffset++
    ) {
      for (
        let xOffset = -chunksVisibleInViewDst;
        xOffset <= chunksVisibleInViewDst;
        xOffset++
      ) {
        const coordX = currentChunkCoordX + xOffset;
        const coordY = currentChunkCoordY + yOffset;

        // Chunk world centre and nearest-edge (horizontal) distance to the
        // viewer — the same metric as bounds.distanceToPoint, computed here
        // without needing the chunk to exist yet.
        const centreX = coordX * chunkSize;
        const centreZ = coordY * chunkSize;
        const dx = Math.max(Math.abs(viewerX - centreX) - half, 0);
        const dz = Math.max(Math.abs(viewerZ - centreZ) - half, 0);
        const dist = Math.sqrt(dx * dx + dz * dz);

        // Beyond the view radius it is never a candidate; the eviction pass
        // below reclaims any such chunk that is already loaded.
        if (dist > maxViewDst) continue;

        const mapId = `${coordX},${coordY}`;
        const existing = this.terrainChunks.get(mapId);

        // An already-loaded chunk is always managed exactly as before —
        // distance-based visibility and LOD. The frustum must NOT hide it: the
        // render pass already frustum-culls what is off-screen, and hiding
        // loaded terrain here made it vanish (and re-stream) each time the
        // camera turned away and back. The frustum only decides what to
        // *generate* (the creation branch below), never what stays on screen.
        if (existing) {
          existing.updateTerrainChunk(
            this.viewerPosition,
            this.viewerEye,
            this,
            renderer
          );

          if (existing.visible) {
            existing.visibilityPass = pass;
            this.terrainChunksVisibleLastUpdate.push(existing);
          }
          continue;
        }

        // Not created yet: only spawn (and generate) it when it is worth it —
        // within the near/physics radius (always, regardless of facing) or
        // actually in view. This is the "load what the viewer sees first, never
        // generate terrain the player hasn't looked at" behaviour. Once created
        // it persists and is managed above until distance eviction disposes it.
        let wanted = dist <= nearForceLoadDst;
        if (!wanted) {
          _cullBox.min.set(
            centreX - half,
            -CULL_BOX_HALF_HEIGHT,
            centreZ - half
          );
          _cullBox.max.set(
            centreX + half,
            CULL_BOX_HALF_HEIGHT,
            centreZ + half
          );
          wanted = _frustum.intersectsBox(_cullBox);
        }
        if (!wanted) continue;

        const newChunk = new TerrainChunk(
          new Vector2(coordX, coordY),
          chunkSize,
          this.mapChunkSizeLod,
          this.detailLevels,
          this.seed,
          this.climatePreset,
          this.seaLevel
        );

        newChunk.dispatcher.add(this.onChunkLoadedDelegate);
        newChunk.visible = false;
        renderer.scene.addChild(newChunk.transform);
        this.terrainChunks.set(mapId, newChunk);
        newChunk.updateTerrainChunk(
          this.viewerPosition,
          this.viewerEye,
          this,
          renderer
        );
      }
    }

    for (const chunk of previous) {
      if (chunk.visibilityPass !== pass) chunk.visible = false;
    }

    // Runs after visible chunk processing so we never evict something that
    // was just visited above.
    this.evictDistantChunks(renderer);
  }

  private evictDistantChunks(renderer: Renderer) {
    const maxViewDst = this.maxViewDst;
    const evictDistance = this.evictDistance;
    const viewerPos = this.viewerPosition;
    const toFullEvict: string[] = [];

    for (const [key, chunk] of this.terrainChunks) {
      // Same nearest-edge metric as chunk visibility/LOD selection — using
      // center distance here would unload chunks that are still visible.
      const dist = chunk.bounds.distanceToPoint(viewerPos);
      if (dist > evictDistance) {
        toFullEvict.push(key);
      } else if (dist > maxViewDst) {
        const hadGPU = chunk.lodMesh.some((lod) => lod.gpuState === 'ready');
        chunk.unloadGPU();
        if (hadGPU) {
          this.dispatcher.dispatch({ type: 'chunk-unloaded', chunk });
        }
      }
    }

    for (const key of toFullEvict) {
      const chunk = this.terrainChunks.get(key)!;
      this.dispatcher.dispatch({ type: 'chunk-disposed', chunk });
      chunk.dispatcher.remove(this.onChunkLoadedDelegate);
      chunk.dispose();
      renderer.scene.removeChild(chunk.transform);
      this.terrainChunks.delete(key);
    }
  }

  // Terrain height at world (x, z), bilinearly sampled from the owning
  // chunk's in-memory LOD-0 heightfield; null when that chunk has no heights
  // yet. Exact regardless of terrain altitude (unlike a bounded raycast) and
  // reflects in-flight sculpt edits immediately — the mesh lags a rebuild
  // behind. Used by the editor's orbit-camera ground clamp.
  sampleHeight(x: number, z: number): number | null {
    const span = this.chunkSize; // world units per chunk
    if (!span) return null;

    const cx = Math.round(x / span);
    const cy = Math.round(z / span);
    const heights = this.terrainChunks.get(`${cx},${cy}`)?.heights;
    if (!heights) return null;
    return this.chunkHeight(heights, cx, cy, x, z);
  }

  /**
   * Writes the splat weights at world (x, z) into `out`, `MAX_SPLAT_LAYERS` of
   * them, 0..1, over `splatPalette`; painted edits included. False, with `out`
   * untouched, when the owning chunk has no splat yet.
   */
  sampleSplat(x: number, z: number, out: Float32Array): boolean {
    const span = this.chunkSize;
    if (!span) return false;
    const cx = Math.round(x / span);
    const cy = Math.round(z / span);
    const splat = this.terrainChunks.get(`${cx},${cy}`)?.splatData;
    if (!splat) return false;
    const mps = this.metersPerSample;
    sampleSplatMap(
      splat,
      this.mapChunkSizeLod,
      (x - cx * span + span / 2) / mps,
      (cy * span + span / 2 - z) / mps,
      out
    );
    return true;
  }

  /** The material of each splat channel, for the climate preset in force. */
  get splatPalette(): readonly string[] {
    this.refreshSplatPalette();
    return this._splatPalette;
  }

  /**
   * How slippery the ground is at world (x, z), 0..1: each material's `slip`
   * weighted by its splat there. 0 where the owning chunk has no splat yet.
   */
  sampleSlip(x: number, z: number): number {
    this.refreshSplatPalette();
    const weights = this._slipWeights;
    if (!this.sampleSplat(x, z, weights)) return 0;
    let slip = 0;
    for (let c = 0; c < MAX_SPLAT_LAYERS; c++)
      slip += weights[c] * this._splatSlips[c];
    return slip;
  }

  // Rebuilds the splat palette and each channel's slip after a preset change.
  private refreshSplatPalette(): void {
    if (this._splatPalettePreset === this._climatePreset) return;
    this._splatPalettePreset = this._climatePreset;
    this._splatPalette = getClimatePalette(
      resolveClimatePreset(this._climatePreset)
    );
    this._splatSlips.fill(0);
    this._splatPalette.forEach(
      (name, c) => (this._splatSlips[c] = TERRAIN_MATERIALS[name]?.slip ?? 0)
    );
  }

  /** The water map of chunk (cx, cy); null where it has none or is not
   *  loaded. */
  waterMapAt(cx: number, cy: number): WaterMap | null {
    return this.terrainChunks.get(`${cx},${cy}`)?.water ?? null;
  }

  // Metres of ocean at world (x, z): sea level less the ground, where the
  // owning chunk's water map holds mostly ocean; 0 on land, in lakes or on
  // unloaded ground. The shore field samples it a grid at a time, so the
  // owning chunk is cached between calls; `resetOceanCache` drops it.
  private sampleOceanDepth(x: number, z: number): number {
    const span = this.chunkSize;
    if (!span || this.oceanType < 0) return 0;
    const cx = Math.round(x / span);
    const cy = Math.round(z / span);
    if (cx !== this.oceanChunkX || cy !== this.oceanChunkY) {
      this.oceanChunkX = cx;
      this.oceanChunkY = cy;
      this.oceanChunk = this.terrainChunks.get(`${cx},${cy}`) ?? null;
    }
    const chunk = this.oceanChunk;
    const water = chunk?.water;
    if (!chunk?.heights || !water) return 0;
    const depth =
      this._seaLevel - this.chunkHeight(chunk.heights, cx, cy, x, z);
    if (depth <= 0) return 0;

    const texels = (this.mapChunkSizeLod - 1) / water.step;
    const mx = Math.round(((x - cx * span + span / 2) / span) * texels);
    const my = Math.round(((cy * span + span / 2 - z) / span) * texels);
    const t =
      Math.min(water.size - 1, Math.max(0, mx)) +
      Math.min(water.size - 1, Math.max(0, my)) * water.size;
    const ocean = water.coverage[t] * water.typeWeights[t * 4 + this.oceanType];
    return ocean >= 0.5 * 255 * 255 ? depth : 0;
  }

  private resetOceanCache() {
    this.oceanChunkX = NaN;
    this.oceanChunkY = NaN;
    this.oceanChunk = null;
  }

  private chunkHeight(
    heights: Float32Array,
    cx: number,
    cy: number,
    x: number,
    z: number
  ): number {
    const span = this.chunkSize;
    const size = this.mapChunkSizeLod;
    // Chunk-local *world* offset (see MeshGenerator: +z is -sy), then to sample
    // space by dividing out the world scale — heights are indexed per sample.
    const mps = this.metersPerSample;
    const maxSample = size - 1;
    const fx = Math.min(
      maxSample,
      Math.max(0, (x - cx * span + span / 2) / mps)
    );
    const fz = Math.min(
      maxSample,
      Math.max(0, (cy * span + span / 2 - z) / mps)
    );
    const x0 = Math.floor(fx);
    const z0 = Math.floor(fz);
    const x1 = Math.min(x0 + 1, maxSample);
    const z1 = Math.min(z0 + 1, maxSample);
    const tx = fx - x0;
    const tz = fz - z0;

    const h00 = heights[z0 * size + x0];
    const h10 = heights[z0 * size + x1];
    const h01 = heights[z1 * size + x0];
    const h11 = heights[z1 * size + x1];
    const top = h00 + (h10 - h00) * tx;
    const bottom = h01 + (h11 - h01) * tx;
    return top + (bottom - top) * tz;
  }

  // The chunk that owns world (x, z) — the same world→chunk mapping
  // sampleHeight uses to pick a heightfield, exposed so a listener can tell
  // which placements a given chunk's heights are responsible for. Null before
  // init(), when chunkSize is still zero.
  chunkIdAt(x: number, z: number): string | null {
    const span = this.chunkSize;
    if (!span) return null;
    return `${Math.round(x / span)},${Math.round(z / span)}`;
  }

  // Surface normal at world (x, z), from a central difference of sampleHeight
  // one sample step either side; false (and out untouched) when the owning
  // chunk has no heights. A probe that lands in an unloaded neighbour falls
  // back to the centre height, so the slope goes one-sided at the seam rather
  // than failing.
  sampleNormal(x: number, z: number, out: Vector3): boolean {
    const h = this.sampleHeight(x, z);
    if (h === null) return false;

    const step = this.metersPerSample;
    const hx0 = this.sampleHeight(x - step, z) ?? h;
    const hx1 = this.sampleHeight(x + step, z) ?? h;
    const hz0 = this.sampleHeight(x, z - step) ?? h;
    const hz1 = this.sampleHeight(x, z + step) ?? h;

    // For a heightfield y = h(x, z) the normal is (-dh/dx, 1, -dh/dz).
    out.set((hx0 - hx1) / (2 * step), 1, (hz0 - hz1) / (2 * step)).normalize();
    return true;
  }

  // Applies an edit: replaces a chunk's in-memory heightfield and rebuilds its
  // meshes, without touching the rest of the terrain. Returns false if the
  // chunk isn't loaded (a saved snapshot will supply the heights when it is).
  applyChunkHeights(cx: number, cy: number, heights: Float32Array): boolean {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    if (!chunk) return false;

    chunk.setHeights(heights);
    if (this.renderer) chunk.refreshMeshes(this.renderer);
    return true;
  }

  // Rebuilds one chunk's meshes from its current in-memory heights after they
  // were mutated in place (a sculpt stamp), without touching the rest of the
  // terrain. Rebuilds run in the background and swap in when ready — the old
  // mesh keeps rendering meanwhile, and listeners get chunk-unloaded /
  // chunk-loaded around each swap (e.g. to rebuild physics colliders).
  // Repeated calls while a rebuild is in flight coalesce. Returns false if
  // the chunk isn't loaded.
  remeshChunk(cx: number, cy: number): boolean {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    if (!chunk || !this.renderer) return false;

    chunk.bumpHeightsVersion();
    chunk.refreshMeshes(this.renderer);
    return true;
  }

  /**
   * Regenerates a chunk's splat map from its current heights and biome mask,
   * and uploads it — the whole map, or just the LOD-0 sample window given.
   *
   * This is the paint brush's counterpart to remeshChunk, and the reason
   * painting is cheap: a stroke changes no geometry, so nothing is re-meshed and
   * no worker is involved. Only the disc under the brush is resolved and only
   * that rectangle is uploaded, so the cost tracks the brush size rather than
   * the chunk size.
   *
   * Returns false when the chunk isn't loaded or has no splat yet (its first
   * worker build will pick the mask up on its own).
   */
  refreshChunkSplat(
    cx: number,
    cy: number,
    window?: { x0: number; y0: number; x1: number; y1: number }
  ): boolean {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    const renderer = this.renderer;
    if (!chunk || !renderer) return false;
    // Nothing resident to patch — the pending/next build generates from the
    // mask anyway, so there is nothing to do and nothing lost.
    if (!chunk.splatData || !chunk.heights || !chunk.splatTexture) return false;

    const size = this.mapChunkSizeLod;
    generateSplatMap(
      size,
      size,
      chunk.seed,
      chunk.noiseOffset,
      resolveClimatePreset(chunk.climatePreset),
      chunk.heights,
      {
        biomeMask: chunk.biomeMask,
        out: chunk.splatData,
        region: window,
        seaLevel: chunk.seaLevel,
      }
    );

    if (window) {
      chunk.uploadSplatRegion(
        renderer,
        window.x0,
        window.y0,
        window.x1,
        window.y1
      );
    } else {
      chunk.uploadSplatRegion(renderer, 0, 0, size - 1, size - 1);
    }
    return true;
  }

  /**
   * Marks a chunk's scatter instances stale after its density mask was edited,
   * so the next visibility update re-places them.
   *
   * Unlike refreshChunkSplat this cannot patch the window under the brush:
   * placement is a worker pass over the whole chunk and an instance is a thing,
   * not a texel. Returns false when the chunk isn't loaded — its first
   * placement reads the saved mask anyway.
   *
   * The visibility pass is what acts on the staleness, and it normally only
   * runs when the camera moves — a brush is the one thing that changes the
   * world while the view holds still, so it has to ask for one.
   */
  refreshChunkScatter(cx: number, cy: number): boolean {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    if (!chunk) return false;
    chunk.bumpScatterInputVersion();
    this._needsVisibilityUpdate = true;
    return true;
  }

  /**
   * A chunk's generated LOD-0 heights, built on a worker for a chunk that is not
   * loaded. The coarsest mesh is built alongside and dropped.
   */
  async generateChunkHeights(cx: number, cy: number): Promise<Float32Array> {
    const size = this.mapChunkSizeLod;
    const { heights } = await this.workerPool.enqueue({
      chunkSize: size,
      lod: this.detailLevels.at(-1)!.lod,
      position: new Vector2(cx * (size - 1), cy * (size - 1)),
      seed: this.seed,
      climatePreset: this.climatePreset,
      seaLevel: this.seaLevel,
    });
    return heights;
  }

  /**
   * Rebuilds a chunk's water map and instances after its water edit changed.
   * Returns false when the chunk isn't loaded — its first build reads the
   * saved edit anyway.
   */
  refreshChunkWater(cx: number, cy: number): boolean {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    if (!chunk) return false;
    chunk.bumpWaterEditVersion();
    this._needsVisibilityUpdate = true;
    return true;
  }

  /**
   * The scatter instance nearest a world-space point, within `radius` metres
   * horizontally, or null when there is none.
   *
   * Re-derives the few cells around the point rather than searching what is
   * drawn — see pickScatterInstance. Chunks whose heights are not in memory yet
   * are skipped: placement reads those heights, so there is nothing to pick
   * against until they land.
   *
   * `includeKilled` ignores the kill sets, so the pick can find an instance
   * that has been plucked — which is how a restore names the key to take back
   * out, since a removed instance is not drawn and has nothing else to point at.
   */
  pickScatter(
    x: number,
    z: number,
    radius: number,
    includeKilled = false
  ): { cx: number; cy: number; pick: ScatterPick } | null {
    const span = this.chunkSize;
    const half = span / 2;
    const cxMin = Math.ceil((x - radius - half) / span);
    const cxMax = Math.floor((x + radius + half) / span);
    const cyMin = Math.ceil((z - radius - half) / span);
    const cyMax = Math.floor((z + radius + half) / span);

    let best: { cx: number; cy: number; pick: ScatterPick } | null = null;

    for (let cy = cyMin; cy <= cyMax; cy++) {
      for (let cx = cxMin; cx <= cxMax; cx++) {
        const chunk = this.terrainChunks.get(`${cx},${cy}`);
        if (!chunk?.heights) continue;

        const pick = pickScatterInstance(
          this.mapChunkSizeLod,
          chunk.seed,
          chunk.noiseOffset,
          resolveClimatePreset(chunk.climatePreset),
          chunk.heights,
          x - cx * span,
          z - cy * span,
          radius,
          {
            biomeMask: chunk.biomeMask,
            scatterMask: chunk.scatterMask,
            killSet: includeKilled ? null : chunk.scatterKills,
            seaLevel: chunk.seaLevel,
          }
        );

        if (pick && (!best || pick.distance < best.pick.distance))
          best = { cx, cy, pick };
      }
    }

    return best;
  }

  /**
   * Plucks one instance: adds its key to the chunk's kill set and marks the
   * chunk's instances stale so the next visibility update re-places without it.
   *
   * Returns the chunk's kill set so the caller can persist it, or null when the
   * chunk isn't loaded or its saved set is still being read — a pluck then does
   * nothing rather than starting a fresh set that the read would overwrite.
   */
  pluckScatter(cx: number, cy: number, key: number): ScatterKillSet | null {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    if (!chunk) return null;

    const kills = chunk.editableScatterKills();
    if (!kills) {
      // Warm the lookup so the next click lands.
      chunk.resolveScatterKills(this.scatterKillProvider);
      return null;
    }

    kills.add(key);
    chunk.bumpScatterInputVersion();
    this._needsVisibilityUpdate = true;
    return kills;
  }

  /**
   * Puts a plucked instance back. Returns the chunk's kill set so the caller can
   * persist it, or null when there was nothing to undo — the chunk isn't
   * loaded, its saved set is still being read, or that instance was never
   * plucked.
   */
  restoreScatter(cx: number, cy: number, key: number): ScatterKillSet | null {
    const chunk = this.terrainChunks.get(`${cx},${cy}`);
    const kills = chunk?.scatterKills;
    if (!chunk || !kills || !kills.delete(key)) return null;

    chunk.bumpScatterInputVersion();
    this._needsVisibilityUpdate = true;
    return kills;
  }

  /**
   * The LOD-0 sample window a world-space disc covers in chunk (cx, cy),
   * padded by one sample so the splat's bilinear filtering has a correct ring
   * around the edited texels. Returns null when the disc misses the chunk.
   *
   * Mirrors sampleHeight's world→sample mapping (+z is −sy), which is the same
   * convention the mesh, the heightfield and the mask all use.
   */
  splatWindowForDisc(
    cx: number,
    cy: number,
    centerX: number,
    centerZ: number,
    radius: number
  ): { x0: number; y0: number; x1: number; y1: number } | null {
    const span = this.chunkSize; // world units per chunk
    const size = this.mapChunkSizeLod;
    const mps = this.metersPerSample;
    const max = size - 1;

    // Chunk-local world offsets of the disc's bounding box, then to samples.
    const localMinX = centerX - radius - cx * span + span / 2;
    const localMaxX = centerX + radius - cx * span + span / 2;
    // +z is −sy, so the far edge in z is the *small* sample row.
    const localMinZ = cy * span + span / 2 - (centerZ + radius);
    const localMaxZ = cy * span + span / 2 - (centerZ - radius);

    const x0 = Math.floor(localMinX / mps) - 1;
    const x1 = Math.ceil(localMaxX / mps) + 1;
    const y0 = Math.floor(localMinZ / mps) - 1;
    const y1 = Math.ceil(localMaxZ / mps) + 1;
    if (x1 < 0 || y1 < 0 || x0 > max || y0 > max) return null;

    return {
      x0: Math.max(0, x0),
      y0: Math.max(0, y0),
      x1: Math.min(max, x1),
      y1: Math.min(max, y1),
    };
  }

  update(renderer: Renderer, camera: Camera) {
    if (!this._enabled) {
      this.underWater.clear(renderer.device);
      return;
    }
    this.updateFarOnly(camera);
    this.updateVisibility(renderer, camera);
    this.updateChunkFades(renderer);
    this.updateHorizonOcean(renderer);
    this.updateWaterWaves(renderer, camera);
  }

  private updateWaterWaves(renderer: Renderer, camera: Camera) {
    const wind = renderer.sky.skyRenderer.wind.vec;
    const palette = resolveClimatePreset(this._climatePreset).water ?? [];
    const deltaSeconds = renderer.delta / 1000;
    const waves = this.waterWaves.waves;
    waves.update(palette);
    if (!this.ocean) this.ocean = new OceanFFT(renderer.device);
    this.ocean.update(
      renderer.device,
      deltaSeconds,
      wind[0],
      wind[1],
      wind[2],
      this.oceanTimer?.writes('ocean')
    );
    this.oceanTimer?.resolve();
    const eye = camera.transform.position;
    waves.setOrigin(eye.x, eye.z);

    this.oceanType = -1;
    for (let i = 0; i < palette.length; i++)
      if (palette[i].name === OCEAN_WATER) this.oceanType = i;
    this.resetOceanCache();
    this.shoreField.update(
      renderer.device,
      deltaSeconds,
      eye.x,
      eye.z,
      this.sampleOceanDepthDelegate
    );
    this.waterLevels.update(
      renderer.device,
      deltaSeconds,
      eye.x,
      eye.z,
      (this.mapChunkSizeLod - 1) / WATER_MAP_STEP,
      this.waterMapAtDelegate,
      palette
    );

    // Distances are measured from where chunk LODs were last chosen, so a
    // vertex's waves always match the coarsest grid that can border it.
    const lodEye = this.viewPosOld ?? eye;
    let finest = waterGridBands(
      this.detailLevels,
      this.chunkSize,
      this.waterLodDistances,
      this.waterLodSpacings
    );
    // A spacing no cascade fits in samples each at its last mip: the mean,
    // which is flat.
    if (!this.waterWaveGeometry) {
      finest = 1e6;
      this.waterLodSpacings.fill(1e6);
    }
    this.waterWaves.upload(renderer.device, {
      detailBias: waterDetailBias(renderer.quality.aspect('water')),
      normals: this.waterWaveNormals,
      foamDebug: this.waterFoamDebug,
      shoreDebug: this.waterShoreDebug,
      refractionDebug: this.waterRefractionDebug,
      crestGlow: this.waterCrestGlow,
      troughDarkening: this.waterTroughDarkening,
      eyeX: lodEye.x,
      eyeZ: lodEye.z,
      finestSpacing: finest,
      lodDistances: this.waterLodDistances,
      lodSpacings: this.waterLodSpacings,
      windSpeed: this.ocean.windSpeed,
      cascadeRms: this.ocean.cascadeRms,
      time: this.ocean.time,
      shoreHeight: shoreWaveHeight(this.ocean.cascadeRms, this.waterShoreWaves),
      shoreCentreX: this.shoreField.centreX,
      shoreCentreZ: this.shoreField.centreZ,
      shoreSpan: SHORE_FIELD_SPAN,
      swash: this.waterSwash,
      wetBand: this.waterWetBand,
    });

    if (!this.seaSpray)
      this.seaSpray = new SeaSpray(
        renderer.device,
        this.ocean,
        this.waterWaves.buffer(renderer.device),
        this.shoreField.texture,
        renderer.samplerManager.get('linear-clamped')
      );
    const sun = renderer.sky.skyRenderer.keyLight;
    const radiance = sun.intensity;
    this.sunRadiance[0] = sun.color.r * radiance;
    this.sunRadiance[1] = sun.color.g * radiance;
    this.sunRadiance[2] = sun.color.b * radiance;
    this.seaSpray.update(
      renderer.device,
      deltaSeconds,
      camera,
      wind,
      this.sunRadiance,
      this.seaLevel,
      (x, z) => this.sampleHeight(x, z)
    );

    const toSun = this.toSun;
    const sunAt = sun.transform.position;
    const sunDistance = sunAt.length() || 1;
    toSun[0] = sunAt.x / sunDistance;
    toSun[1] = sunAt.y / sunDistance;
    toSun[2] = sunAt.z / sunDistance;
    const underWater = this.underWater;
    underWater.update(
      renderer.device,
      this.waterQuery,
      palette,
      eye.x,
      eye.y,
      eye.z,
      toSun,
      this.sunRadiance,
      clipNear(camera.projectionMatrix.elements),
      camera.transform.matrixWorld.elements,
      finest
    );
    const eyeWorld = camera.transform.matrixWorld.elements;
    this.caustics.update(
      renderer.device,
      this.ocean,
      toSun,
      eyeWorld[12],
      eyeWorld[14],
      this.cameraCascadeWeight(CAUSTIC_CASCADE),
      underWater.covered || this.waterInView(),
      this.causticsTimer?.writes('caustics')
    );
    this.causticsTimer?.resolve();
    this.marineSnow.update(deltaSeconds);
    const sky = renderer.sky.skyRenderer;
    this.waterLens.update(
      underWater,
      deltaSeconds,
      lensRain(sky.precipitation, sky.temperature) * rainBelowDeck(eye.y),
      sky.wind.gustDrift,
      eye.x,
      eye.z
    );

    if (!this.waterProbe)
      this.waterProbe = new WaterProbe(
        renderer.device,
        this.ocean,
        this.waterWaves.buffer(renderer.device),
        this.shoreField,
        renderer.samplerManager.get('linear-clamped')
      );
    this.waterProbe.update(
      renderer.device,
      this.waterQuery,
      waves.originX,
      waves.originZ,
      underWater.possible ? underWater.probeCopies : undefined
    );
  }

  // Whether a chunk drawn last update has water.
  private waterInView(): boolean {
    const chunks = this.terrainChunksVisibleLastUpdate;
    for (let i = 0; i < chunks.length; i++) if (chunks[i].water) return true;
    return false;
  }

  // The water at the camera's weight on cascade `c`; 0 out of the water.
  private cameraCascadeWeight(c: number): number {
    const underWater = this.underWater;
    if (!underWater.covered) return 0;
    const types = this.waterWaves.waves.cascadeTypes;
    const weights = underWater.sample.typeWeights;
    let weight = 0;
    for (let t = 0; t < MAX_WATER_TYPES; t++)
      weight += types[c * MAX_WATER_TYPES + t] * weights[t];
    return weight;
  }

  /** Draws water on the lens over the composited scene, before the tonemap. */
  renderLens(
    renderer: Renderer,
    camera: Camera,
    scene: GPUTexture,
    sceneView: GPUTextureView
  ) {
    // The shafts' pass was submitted with the composite.
    this.shaftsTimer?.resolve();
    if (!this._enabled || !renderer.lensEffects) return;
    this.waterLens.render(
      renderer,
      renderer.sky.skyRenderer.wind.vec,
      camera,
      this.underWater,
      scene,
      sceneView,
      this.lensTimer ?? undefined
    );
    this.lensTimer?.resolve();
  }

  /** Marches the light shafts while the camera may be under water. Encode
   *  before the atmosphere composite's pass. */
  renderLightShafts(renderer: Renderer, encoder: GPUCommandEncoder) {
    if (
      !this._enabled ||
      !this.underWater.possible ||
      !underWaterQuality(renderer.quality.aspect('water')).shafts
    ) {
      this.lightShafts.skip();
      return;
    }
    this.lightShafts.march(
      renderer,
      encoder,
      this.underWater.buffer(renderer.device),
      this.caustics,
      this.shaftsTimer?.writes('shafts')
    );
  }

  /** Fogs the view in the water while the camera may be under it, and adds
   *  the light shafts. Draws into the atmosphere composite's `pass`, after the
   *  atmosphere. */
  renderUnderWaterFog(renderer: Renderer, pass: GPURenderPassEncoder) {
    if (!this._enabled || !this.underWater.possible) return;
    this.underWaterFog.draw(
      renderer,
      pass,
      this.underWater.buffer(renderer.device)
    );
    this.lightShafts.composite(pass);
  }

  /** Draws the marine snow over the composited HDR scene in `target` while
   *  the camera may be under water. */
  renderMarineSnow(
    renderer: Renderer,
    encoder: GPUCommandEncoder,
    target: GPUTextureView,
    camera: Camera
  ) {
    if (!this._enabled || !this.underWater.possible || !this.ocean) return;
    const share = underWaterQuality(renderer.quality.aspect('water')).snowShare;
    const waves = this.waterWaves.waves;
    this.marineSnow.draw(
      renderer,
      encoder,
      target,
      camera,
      share,
      this.underWater.buffer(renderer.device),
      this.waterWaves.buffer(renderer.device),
      this.ocean,
      this.caustics,
      waves.originX,
      waves.originZ,
      this.cameraCascadeWeight(0)
    );
  }

  /** Draws the sea spray over the composited HDR scene in `target`. */
  renderSeaSpray(
    renderer: Renderer,
    encoder: GPUCommandEncoder,
    target: GPUTextureView
  ) {
    if (this.underWater.submerged) return;
    this.seaSpray?.draw(renderer, encoder, target);
  }

  private updateChunkFades(renderer: Renderer) {
    const frame = ++this.fadeFrame;
    const now = performance.now();
    const chunks = this.terrainChunksVisibleLastUpdate;
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (chunk.drawn)
        chunk.updateFade(frame, now, this.heightFade, renderer.device);
    }
  }

  // The ring follows the centre chunk visibility was last computed from, so it
  // starts exactly where the chunks stop.
  private updateHorizonOcean(renderer: Renderer) {
    if (!this.horizonOcean || !this.viewPosOld) return;
    if (this.horizonClimate?.preset !== this._climatePreset)
      this.horizonClimate = {
        preset: this._climatePreset,
        climate: resolveClimatePreset(this._climatePreset),
      };
    const chunkSize = this.chunkSize;
    const originX = Math.round(this.viewPosOld.x / chunkSize);
    const originZ = Math.round(this.viewPosOld.z / chunkSize);
    const mask = this.horizonChunkMask;
    mask.fill(0);
    const chunks = this.terrainChunksVisibleLastUpdate;
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (!chunk.drawn) continue;
      const index = chunkMaskIndex(
        chunk.coord.x,
        chunk.coord.y,
        originX,
        originZ
      );
      if (index >= 0) mask[index] = Math.round(chunk.fade * 255);
    }

    this.horizonOcean.update(renderer, {
      seed: this._seed,
      climatePreset: this._climatePreset,
      climate: this.horizonClimate.climate,
      seaLevel: this._seaLevel,
      centreX: this.viewPosOld.x,
      centreZ: this.viewPosOld.z,
      maxViewDst: this.maxViewDst,
      chunkSize,
      chunkMask: mask,
      windSpeed: this.ocean?.windSpeed ?? 0,
      slopeScale: this.waterHorizonSlope,
    });
  }

  // High up, the chunks under the camera are too far to show their detail and
  // a fast camera streams them faster than they build, so the ring draws all
  // the ground instead. Chunks fade in by height on the way down, so they
  // arrive with the descent rather than at once. Far-only starts a margin above
  // where they have faded out, so hovering there does not stream them in and
  // out.
  private updateFarOnly(camera: Camera) {
    const eye = camera.transform.position;
    const height = eye.y - this.generatedGroundHeight(eye.x, eye.z);
    const above = this.farOnlyAbove;
    const band = Math.max(1, above - this.farOnlyBelow);
    this.heightFade = Math.min(1, Math.max(0, (above - height) / band));
    if (!this._farOnly && height > above + FAR_ONLY_MARGIN) this.enterFarOnly();
    else if (this._farOnly && height < above) {
      this._farOnly = false;
      this._farOnlyReported = false;
      this._needsVisibilityUpdate = true;
    }
  }

  // Chunks keep their CPU heights and geometry, so coming back down re-shows
  // the ones still in range without a worker rebuild.
  private enterFarOnly() {
    this._farOnly = true;
    for (const chunk of this.terrainChunks.values()) {
      chunk.visible = false;
      let hadGPU = false;
      for (const lod of chunk.lodMesh) hadGPU ||= lod.gpuState === 'ready';
      chunk.unloadGPU();
      if (hadGPU) this.dispatcher.dispatch({ type: 'chunk-unloaded', chunk });
    }
    this.terrainChunksVisibleLastUpdate.length = 0;
  }

  /** The generator's ground at world (x, z), or the sea over it, with no chunk
   *  needed. Ignores edits, so it is for coarse checks such as camera height. */
  generatedGroundHeight(x: number, z: number): number {
    this.groundSampler ??= createGroundSampler(
      this._seed,
      resolveClimatePreset(this._climatePreset),
      this._seaLevel
    );
    const mps = this.metersPerSample;
    const h = sampleGround(this.groundSampler, x / mps - 0.5, -z / mps - 0.5);
    return Math.max(h, this._seaLevel);
  }

  private updateVisibility(renderer: Renderer, camera: Camera) {
    // getWorldDirection refreshes the camera's world matrix and, via its
    // transform observer, matrixWorldInverse — so both the frustum built below
    // and the facing comparison use this frame's camera pose.
    camera.getWorldDirection(_viewDir);

    this.viewerPosition.set(
      camera.transform.position.x,
      0,
      camera.transform.position.z
    );
    this.viewerEye.copy(camera.transform.position);

    // The view chunk selection generates terrain for: the camera's, widened by
    // GENERATION_VIEW_MARGIN, so terrain the player cannot see is still
    // skipped. One frame stale at worst, which coarse chunk streaming
    // tolerates.
    _generationProj.copy(camera.projectionMatrix);
    widenProjection(_generationProj, GENERATION_VIEW_MARGIN);
    _projScreenMatrix.multiplyMatrices(
      _generationProj,
      camera.matrixWorldInverse
    );
    _frustum.setFromProjectionMatrix(_projScreenMatrix, WebGPUCoordinateSystem);

    if (!this.viewPosOld) {
      this.viewPosOld = new Vector3();
      this.viewPosOld.copy(this.viewerPosition);
      this.viewDirOld = new Vector3();
      this.viewDirOld.copy(_viewDir);
    }

    if (this._farOnly) {
      if (!this._farOnlyReported) {
        this._farOnlyReported = true;
        this.dispatcher.dispatch({ type: 'far-only' });
      }
      if (
        this.viewPosOld.distanceToSquared(this.viewerPosition) >
        sqrViewerMoveThresholdForChunkUpdate
      ) {
        this.viewPosOld.copy(this.viewerPosition);
        this.evictDistantChunks(renderer);
      }
      return;
    }

    if (!this._hasInitiallyUpdatedTerrain) {
      this.updateVisibleChunks(renderer);

      if (this.terrainChunksVisibleLastUpdate.length > 0)
        this._hasInitiallyUpdatedTerrain = true;
      return;
    }

    const moved =
      this.viewPosOld.distanceToSquared(this.viewerPosition) >
      sqrViewerMoveThresholdForChunkUpdate;
    // Facing changed enough to alter frustum membership (unit vectors, so the
    // dot product is cos of the turn angle).
    const turned =
      this.viewDirOld.dot(_viewDir) < viewDirDotThresholdForChunkUpdate;

    if (moved || turned || this._needsVisibilityUpdate) {
      this.viewPosOld.copy(this.viewerPosition);
      this.viewDirOld.copy(_viewDir);
      this._needsVisibilityUpdate = false;
      this.updateVisibleChunks(renderer);
    }
  }

  render(renderer: Renderer, pass: GPURenderPassEncoder, camera: Camera) {}

  reset(seed: number, renderer: Renderer) {
    this.dispose();
    // The old world's records name lakes by cell, which the new world reuses.
    this.waterRules.clearRecords();
    this.init(renderer);
    this.seed = seed;
  }

  /** Drops every chunk and saved water record, so they rebuild from the
   *  current providers. GPU systems and workers are kept. */
  reload() {
    this.clearChunks();
    this.waterRules.clearRecords();
  }

  private clearChunks() {
    const dispatcher = this.dispatcher;
    for (const chunk of this.terrainChunks.values()) {
      dispatcher.dispatch({ type: 'chunk-disposed', chunk });
      chunk.dispatcher.remove(this.onChunkLoadedDelegate);
      chunk.dispose();
      chunk.transform.removeFromParent();
    }
    this.terrainChunks.clear();
    this.terrainChunksVisibleLastUpdate.length = 0;
    this.groundSampler = null;
    this._farOnlyReported = false;
    this._hasInitiallyUpdatedTerrain = false;
    this._needsVisibilityUpdate = false;
  }

  dispose() {
    this.clearChunks();
    this.horizonOcean?.dispose();
    this.horizonOcean = null;
    this.shoreField?.dispose();
    this.waterWaves.dispose();
    this.seaSpray?.dispose();
    this.seaSpray = null;
    this.waterProbe?.dispose();
    this.waterProbe = null;
    this.waterLens.dispose();
    this.oceanTimer?.dispose();
    this.causticsTimer?.dispose();
    this.shaftsTimer?.dispose();
    this.lensTimer?.dispose();
    this.oceanTimer = null;
    this.causticsTimer = null;
    this.shaftsTimer = null;
    this.lensTimer = null;
    this.lightShafts.dispose();
    this.marineSnow.dispose();
    this.ocean?.dispose();
    this.ocean = null;
    this.scatterModels?.dispose();
    this.workerPool?.dispose();
  }
}
